/**
 * <plp-load-more> — appends the next paginated page of collection products
 * to #product-grid via the Section Rendering API.
 *
 * The server keeps normal {% paginate %} pagination (also used as the
 * noscript fallback), so each fetch is a real collection page with the
 * live request's filter and sort params already in paginate.next.url.
 * The element lives inside #ProductGridContainer, so facets.js filter and
 * sort re-renders naturally replace it with a fresh, correctly-scoped one.
 */
if (!customElements.get('plp-load-more')) {
  class PlpLoadMore extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';

      this.button = this.querySelector('.plp-load-more__button');
      this.label = this.querySelector('.plp-load-more__label');
      this.status = this.querySelector('.plp-load-more__status');
      this.busy = false;

      // Rebind the glass-button spotlight for instances inserted after
      // DOMContentLoaded (e.g. after a facets.js grid re-render).
      document.dispatchEvent(new CustomEvent('ds:init-glass-buttons', { detail: { root: this } }));

      if (!this.dataset.nextUrl) {
        this.hidden = true;
        return;
      }

      this.button?.addEventListener('click', () => this.loadNext());
    }

    setState(state) {
      this.classList.toggle('is-loading', state === 'loading');
      if (this.button) this.button.disabled = state === 'loading';
      if (this.label) {
        if (state === 'loading') {
          this.label.textContent = 'Loading…';
        } else if (state === 'error') {
          this.label.textContent = 'Something went wrong — try again';
        } else {
          this.label.textContent = 'Load more';
        }
      }
    }

    async loadNext() {
      if (this.busy || !this.dataset.nextUrl) return;
      this.busy = true;
      this.setState('loading');

      try {
        const pageUrl = new URL(this.dataset.nextUrl, window.location.origin);
        const fetchUrl = new URL(pageUrl);
        fetchUrl.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(fetchUrl.toString());
        if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

        const responseText = await response.text();

        // A facets.js filter/sort re-render during this fetch replaces
        // #ProductGridContainer, detaching this element. Appending page-N of
        // the OLD filter state into the freshly filtered grid would inject
        // stale products, so bail once we are no longer in the document.
        if (!this.isConnected) return;

        const doc = new DOMParser().parseFromString(responseText, 'text/html');
        const incomingGrid = doc.getElementById('product-grid');
        const grid = document.getElementById('product-grid');
        if (!incomingGrid || !grid || !grid.isConnected) throw new Error('Load more: product grid missing');

        let items = Array.from(incomingGrid.querySelectorAll(':scope > li.grid__item'));

        // Random PLP order: shuffle each incoming batch too (already-loaded
        // products are never moved). No-ops when the shopper picked a sort.
        const randomOrder = window.PlpRandomOrder;
        if (randomOrder?.shouldShuffle()) {
          items = randomOrder.shuffleItems(items);
        }

        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(item));
        grid.appendChild(fragment);

        if (this.status) {
          this.status.textContent = `${items.length} more products loaded`;
        }

        // Deliberately no history/URL update: a ?page=N URL server-renders
        // only page N, so a shopper reloading it would lose every product
        // loaded before it. Reload restarts cleanly from the top instead.

        const incomingLoadMore = doc.querySelector('plp-load-more');
        const upcomingUrl = incomingLoadMore?.dataset.nextUrl;
        if (upcomingUrl) {
          this.dataset.nextUrl = upcomingUrl;
          this.setState('idle');
        } else {
          delete this.dataset.nextUrl;
          this.hidden = true;
        }
      } catch (error) {
        this.setState('error');
      } finally {
        this.busy = false;
      }
    }
  }

  customElements.define('plp-load-more', PlpLoadMore);
}
